/**
 * 对话框工具函数
 * 提供防重复点击和防抖功能
 */

import { Dialog } from 'antd-mobile';

// ==================== 常量定义 ====================

/** 对话框防抖时间（毫秒）- 防止快速点击导致多个对话框 */
const DIALOG_DEBOUNCE_DURATION = 350;

// ==================== 防抖管理器 ====================

/**
 * 对话框防抖管理器
 * 用于防止快速点击导致多个对话框同时显示
 */
class DialogDebounceManager {
    private static activeDialogs = new Set<string>();
    private static lastShowTime = new Map<string, number>();

    /**
     * 检查是否可以显示对话框
     * @param dialogKey 对话框唯一标识
     * @returns 是否可以显示
     */
    static canShow(dialogKey: string): boolean {
        const now = Date.now();
        const lastTime = this.lastShowTime.get(dialogKey) || 0;
        
        // 如果距离上次显示时间太短，不允许显示
        if (now - lastTime < DIALOG_DEBOUNCE_DURATION) {
            return false;
        }

        // 如果已经有相同key的对话框在显示，不允许显示
        if (this.activeDialogs.has(dialogKey)) {
            return false;
        }

        return true;
    }

    /**
     * 标记对话框已显示
     * @param dialogKey 对话框唯一标识
     */
    static markShown(dialogKey: string): void {
        this.activeDialogs.add(dialogKey);
        this.lastShowTime.set(dialogKey, Date.now());
    }

    /**
     * 标记对话框已关闭
     * @param dialogKey 对话框唯一标识
     */
    static markClosed(dialogKey: string): void {
        this.activeDialogs.delete(dialogKey);
    }

    /**
     * 清除所有对话框状态（用于页面切换等场景）
     */
    static clearAll(): void {
        this.activeDialogs.clear();
        this.lastShowTime.clear();
    }
}

// ==================== 工具函数 ====================

/**
 * 显示对话框（带防抖保护）
 * @param dialogKey 对话框唯一标识（用于防重复）
 * @param config 对话框配置
 * @returns 对话框处理器，如果被防抖则返回 null
 */
export function showDialogWithDebounce(
    dialogKey: string,
    config: Parameters<typeof Dialog.show>[0]
): ReturnType<typeof Dialog.show> | null {
    // 检查是否可以显示
    if (!DialogDebounceManager.canShow(dialogKey)) {
        console.warn(`对话框 ${dialogKey} 被防抖，忽略重复显示`);
        return null;
    }

    // 标记已显示
    DialogDebounceManager.markShown(dialogKey);

    // 显示对话框
    const handler = Dialog.show({
        ...config,
        onClose: () => {
            // 标记已关闭
            DialogDebounceManager.markClosed(dialogKey);
            // 调用原始 onClose（如果存在）
            if (config.onClose) {
                config.onClose();
            }
        },
        closeOnAction: config.closeOnAction !== false // 默认关闭
    });

    // 如果对话框被关闭，标记已关闭
    // 注意：Dialog.show 返回的 handler 可能没有 close 事件，所以我们在 onClose 中处理

    return handler;
}

/**
 * 清除所有对话框状态
 * 在页面切换或组件卸载时调用
 */
export function clearAllDialogs(): void {
    DialogDebounceManager.clearAll();
}

